import { createRef, useState } from 'react';
import {
  Anchor,
  Button,
  Dropdown,
  Form,
  FormControl,
  InputGroup,
  Modal,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getSeaLinkBasename, phoneNumberLink } from 'utils/helpers';
import useBootStrapMediaQuery from 'utils/hooks/use_bootstrap_media_query';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import useIsLoggedIn from 'utils/hooks/use_is_logged_in';
import { getUserNavigationLinks } from '../utils/navigation_links';

export const SearchModal = ({ isMobile, show, onHide }) => {
  const searchRef = createRef();
  const baseUrl = window.ecom?.craftConfig?.api_prefix || '';

  return (
    <Modal
      show={show}
      onShow={() => searchRef.current.focus()}
      onHide={onHide}
      centered
      backdropClassName="header__modal-dialog--search__backdrop opacity-75"
      dialogClassName="header__modal-dialog--search"
    >
      <Form action={`${baseUrl}/search`}>
        <InputGroup>
          <FormControl
            placeholder="Your search terms"
            name="q"
            className={classNames(!isMobile && 'form-control-lg')}
            aria-label="Search"
            ref={searchRef}
          />
          <Button type="submit" className={classNames(!isMobile && 'btn-lg')}>
            <i className="far fa-search icon__before-text" />
            Search
          </Button>
        </InputGroup>
      </Form>
    </Modal>
  );
};

SearchModal.propTypes = {
  isMobile: PropTypes.bool,
  show: PropTypes.bool,
  onHide: PropTypes.func,
};

export const HeaderIcons = ({ isMobile }) => {
  const { isEditing, isBookingPayable } = useCurrentBookingState();
  const client = useSelector((state) => state.user || {});
  const {
    vendor_id: vendorId,
    display_name: clientDisplayName,
    client_type_name: clientTypeName,
  } = client;
  const isLoggedIn = useIsLoggedIn();
  const [searchOpen, setSearchOpen] = useState(false);
  const basename = getSeaLinkBasename();
  const { features } = window.ecom.craftConfig;

  const userIconDropdownNav = getUserNavigationLinks(isLoggedIn, vendorId);

  return (
    <div className="header__icons">
      {!isMobile && (
        <button
          type="button"
          className="menu-dropdown__toggle header__icon"
          aria-label="search"
          onClick={() => setSearchOpen(true)}
        >
          <i className="far fa-search fa-lg text-white" />
        </button>
      )}

      <a href={`tel:${phoneNumberLink}`} className="header__icon">
        <i className="fas fa-phone-alt fa-lg text-white" />
      </a>

      {!features.hideAccount && (
        <Dropdown className="header__icon-dropdown">
          <Dropdown.Toggle
            as="button"
            aria-label="Open my account menu"
            id="user-icon-dropdown-button"
            className="header__icon"
          >
            <i className="fas fa-user fa-lg text-white" />
            {isLoggedIn && (
              <span className="fa-stack header__fa-stack">
                <i className="fa-solid fa-circle fa-stack-1x header__icon-circle" />
                <i className="fa-solid fa-circle-check fa-stack-1x fa-inverse header__icon-check" />
              </span>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu>
            {clientDisplayName && (
              <>
                <Dropdown.ItemText>{clientDisplayName}</Dropdown.ItemText>
                {clientTypeName && (
                  <Dropdown.ItemText>
                    Account type: {clientTypeName}
                  </Dropdown.ItemText>
                )}
                <Dropdown.Divider />
              </>
            )}
            {userIconDropdownNav.map((nav) => (
              <Dropdown.Item
                key={nav.path}
                as={nav.externalUrl ? 'a' : Anchor}
                href={nav.path}
                className="text-decoration-underline"
              >
                {nav.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      )}

      {/* Only display when item is in 'cart' and is not editing */}
      {!isEditing && isBookingPayable && (
        <a
          className="header__icon header__icon-cart"
          href={`/${basename}/bookings`}
          title="Visit checkout"
        >
          <i className="fas fa-shopping-cart fa-lg text-white">
            <span className="sr-only">Visit checkout</span>
          </i>
        </a>
      )}
      <SearchModal
        show={searchOpen}
        onHide={() => setSearchOpen(false)}
        isMobile={isMobile}
      />
    </div>
  );
};

export const HeaderIconsWrapper = () => {
  const { downLg } = useBootStrapMediaQuery();

  return <HeaderIcons isMobile={downLg} />;
};

HeaderIcons.propTypes = {
  isMobile: PropTypes.bool,
};
